import React from 'react';
import { cn } from '@rocco/utils/cn';
import { Button as RoccoButton } from '@rocco/components/button';
import { ReactComponent as FiltersIcon } from '@rocco/icons/svg/filters.svg';
import { Badge } from '@rocco/components/badge';
import { DisplayMode } from '@rocco/types/enum/display-mode';

interface FiltersButtonProps {
    activeFiltersCount: number;
    displayMode: DisplayMode;
    /** Button variant - 'default' uses dark icon-only, 'primary' uses black background with "Filters" text */
    variant?: 'default' | 'primary';
    /** Optional className to apply to the trigger button (e.g. to override padding). */
    buttonClassName?: string;
    /** Label for the primary variant (defaults to "Filters"). */
    label?: string;
}

export const FiltersButton = React.forwardRef<
    HTMLDivElement,
    FiltersButtonProps
>((props, ref) => {
    const {
        activeFiltersCount,
        displayMode,
        variant = 'default',
        buttonClassName,
        label,
        ...rest
    } = props;
    const hasActiveFilters = activeFiltersCount > 0;

    return (
        <div {...rest} ref={ref} className="relative">
            {displayMode === DisplayMode.Desktop ? (
                <FiltersButtonDesktop
                    variant={variant}
                    hasActiveFilters={hasActiveFilters}
                    buttonClassName={buttonClassName}
                    label={label}
                />
            ) : (
                <FiltersButtonMobile />
            )}

            {hasActiveFilters && (
                <div className="absolute -top-0.5 -right-1 z-20">
                    <Badge size="square" text="default">
                        {activeFiltersCount}
                    </Badge>
                </div>
            )}
        </div>
    );
});

FiltersButton.displayName = 'FiltersButton';

const FiltersButtonMobile = () => (
    <RoccoButton color="muted" size="square42" aria-label="Filter Button">
        <FiltersIcon className="size-4.5 fill-gray-50 text-gray-50" />
    </RoccoButton>
);

const FiltersButtonDesktop = ({
    variant = 'default',
    hasActiveFilters = false,
    buttonClassName,
    label = 'Filters',
}: {
    variant?: 'default' | 'primary';
    hasActiveFilters?: boolean;
    buttonClassName?: string;
    label?: string;
}) => {
    if (variant === 'primary') {
        return (
            <RoccoButton color="dark" className={buttonClassName}>
                <FiltersIcon className="mr-2 size-4.5" />
                <span>{label}</span>
            </RoccoButton>
        );
    }
    return (
        <RoccoButton
            color="secondary"
            weight="normal"
            className={cn(
                buttonClassName,
                hasActiveFilters &&
                    'border-[#161616] bg-[#161616] text-white hover:border-[#2a2a2a] hover:bg-[#2a2a2a]',
            )}
        >
            <span className={cn(hasActiveFilters && 'font-medium')}>
                Show all Filters
            </span>
            <FiltersIcon
                className={cn(
                    'ml-2 size-4.5',
                    hasActiveFilters ? 'fill-white text-white' : 'text-black',
                )}
            />
        </RoccoButton>
    );
};
